import { useMemo } from 'react';

import {
  ActivityBuilderIntroFooter,
  ActivityBuilderIntroHeader,
} from '../introduction/activity-builder-intro';
import ComponentsPrintView from '../components/components-print-view';
import * as Styled from './activity-builder-a4.style';
import {
  INTRODUCTION_INDEX,
  useActivityBuilderApollo,
} from '../activity-builder.apollo';
import { groupSections } from './activity-builder-a4.utils';
import { ActivityBuilderSlideContent } from '../activity-builder-slide.style';
import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import { DraftLessonActivities } from '../activity-builder-draft.constants';
import type { InteractiveLessonSectionFragment } from '../lesson-builder.graphql.generated';
import ActivityBuilderIntroductionPrint from 'approot/activity-builder/introduction/activity-builder-intro-print';
import ActivityPrint from '../components/activity/activity-print';
import { LANGUAGE_CODE_EN_AU } from 'global/constants';

const ActivityBuilderA4 = ({
  pageIndex,
  interactiveLesson,
  sections,
  contentVersion,
}: {
  pageIndex: number;
  interactiveLesson: LessonActivitiesFragment | DraftLessonActivities;
  sections: InteractiveLessonSectionFragment[];
  contentVersion: string | null | undefined;
}) => {
  const {
    data: {
      printSettings: { slidesPerPage, orientation, slides },
    },
  } = useActivityBuilderApollo();

  const { sortedSections } = useMemo(() => {
    return groupSections({
      sections,
      slidesToDisplay: slides,
      slidesPerPage,
      isSinglePage: true,
      currentPage: pageIndex,
    });
  }, [sections, slidesPerPage, pageIndex, slides]);

  const lesson = interactiveLesson.lesson;

  const showCover = slides.includes(INTRODUCTION_INDEX) && pageIndex === 0;

  return (
    <Styled.A4 $orientation={orientation}>
      {showCover && (
        <Styled.A4Page $orientation={orientation}>
          <ActivityBuilderIntroductionPrint
            interactiveLesson={interactiveLesson}
            language={LANGUAGE_CODE_EN_AU}
          />
        </Styled.A4Page>
      )}

      {sortedSections.map((group, index) => {
        return (
          <Styled.A4Page $orientation={orientation} key={index}>
            <Styled.A4Slides>
              {group.map(section => {
                return (
                  <Styled.A4Slide
                    $twoPerPage={slidesPerPage === 2}
                    $orientation={orientation}
                    key={section.id}
                  >
                    <ActivityBuilderIntroHeader
                      unitName={lesson.unit.name}
                      contentName={lesson.name}
                    />
                    <ActivityBuilderSlideContent>
                      <ActivityPrint
                        section={section}
                        sectionsOverview={interactiveLesson?.sectionsOverview}
                      />
                      <ComponentsPrintView
                        section={section}
                        contentId={interactiveLesson.id}
                        isPrintPreview
                        language={LANGUAGE_CODE_EN_AU}
                        contentVersion={contentVersion}
                      />
                    </ActivityBuilderSlideContent>
                    <ActivityBuilderIntroFooter
                      pageNumber={pageIndex + 1}
                      subject={lesson.subjects?.[0]?.label}
                      showGoogleAttribution={false}
                    />
                  </Styled.A4Slide>
                );
              })}
            </Styled.A4Slides>
          </Styled.A4Page>
        );
      })}

      <Styled.A4GlobalStyles $orientation={orientation} />
    </Styled.A4>
  );
};

export default ActivityBuilderA4;
